import { logMessage } from './logMessage';
import { z } from 'zod';

const stringSchema = z.string();

// Webpack-era chunk errors - leaving for now but can likely be removed
const REGEX_JS_CHUNK = /Loading chunk [\d]+ failed/;
const REGEX_CSS_CHUNK = /Loading CSS chunk [\d]+ failed/;

// Vite errors. A deploy replaces the hashed asset files, so browsers holding a
// pre-deploy index.html request chunks that no longer exist. The failure
// message differs per browser and per asset type, so match all known shapes
// (Chrome, Firefox, and Safari word the dynamic-import failure differently).
const REGEX_JS_DYNAMIC_MODULE = /Failed to fetch dynamically imported module/;
const REGEX_JS_DYNAMIC_MODULE_FIREFOX = /error loading dynamically imported module/;
const REGEX_JS_MODULE_SAFARI = /Importing a module script failed/;
const REGEX_JS_MODULE = /Failed to load module script/;
const REGEX_JS_TYPE_ERROR = /'text\/html' is not a valid JavaScript MIME type./;
const REGEX_CSS_PRELOAD = /Unable to preload CSS/;

const STALE_DEPLOY_REGEXES = [
  REGEX_JS_CHUNK,
  REGEX_CSS_CHUNK,
  REGEX_JS_DYNAMIC_MODULE,
  REGEX_JS_DYNAMIC_MODULE_FIREFOX,
  REGEX_JS_MODULE_SAFARI,
  REGEX_JS_MODULE,
  REGEX_JS_TYPE_ERROR,
  REGEX_CSS_PRELOAD,
];

const RELOAD_AT_STORAGE_KEY = 'ML_STALE_DEPLOY_RELOAD_AT';

// If a reload triggered by a stale-deploy error happened this recently and the
// app is failing again, reloading is not fixing it (e.g. index.html itself is
// cached or the CDN is misbehaving) — stop so the user is not stuck in a loop.
const RELOAD_LOOP_WINDOW_MS = 30_000;

/**
 * Returns true when the error is a stale-deploy asset failure: the running app
 * references a hashed JS/CSS chunk that a newer deploy has replaced.
 */
export const isStaleDeployError = (cause: unknown): boolean => {
  const parsedMessage = stringSchema.safeParse(cause);
  const message = cause instanceof Error ? cause.message : parsedMessage.success ? parsedMessage.data : '';
  return STALE_DEPLOY_REGEXES.some((regex) => regex.test(message));
};

const getLastReloadAt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const setLastReloadAt = (timestamp: number): void => {
  try {
    window.sessionStorage.setItem(RELOAD_AT_STORAGE_KEY, String(timestamp));
  } catch {
    // Storage unavailable (e.g. blocked third-party iframe): reload anyway and
    // accept that the loop guard is best-effort there.
  }
};

// One failed preload commonly emits several stale-deploy errors in the same
// page instance (the chunk's CSS and JS, parallel imports) and
// window.location.reload() does not halt script execution, so later errors
// arrive while the reload is in flight. Track that in memory: those errors are
// handled (the page is going away), NOT a sign that reloading failed. The
// sessionStorage timestamp only guards across page loads.
let reloadInFlight = false;

/** Test-only: clears the in-memory reload-in-flight flag to simulate a fresh page load. */
export const resetStaleDeployStateForTesting = (): void => {
  reloadInFlight = false;
};

/**
 * Reloads the page to pick up the freshly deployed assets. Returns true when a
 * reload is happening (or already in flight for this page), and false when a
 * previous page load already reloaded within the loop-guard window, so callers
 * can fall back to an error UI instead of reloading forever.
 */
export const reloadForStaleDeploy = (cause: unknown): boolean => {
  if (reloadInFlight) {
    return true;
  }
  const now = Date.now();
  if (now - getLastReloadAt() < RELOAD_LOOP_WINDOW_MS) {
    logMessage('Stale deploy reload suppressed to avoid a reload loop.', 'warning', { cause: String(cause) });
    return false;
  }
  reloadInFlight = true;
  setLastReloadAt(now);
  logMessage('Stale deploy asset error. App reloaded.', 'info', { cause: String(cause) });
  window.location.reload();
  return true;
};

/**
 * Installs a window listener for Vite's `vite:preloadError` event, which fires
 * when a dynamically imported chunk or its CSS fails to load (the typical
 * symptom right after a deploy replaces hashed assets). Reloading fetches the
 * new index.html and assets; without this the failure surfaces as an uncaught
 * error and the user sees the "Something went wrong" screen. Call once from
 * each app entry point.
 */
export const installStaleDeployReloadHandler = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleDeploy(event.payload)) {
      // Swallow the error only when a reload is actually happening; otherwise
      // let it propagate so the ErrorBoundary can show its fallback UI.
      event.preventDefault();
    }
  });
};
